import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { NotificationContext } from './notification-context';
let counter = 0;
function nextId() {
    counter += 1;
    return `n_${Date.now().toString(36)}_${counter}`;
}
/**
 * Haelt den app-uebergreifenden Benachrichtigungs-Zustand. Quelle-agnostisch:
 * Eintraege kommen via `add()` herein — egal ob aus dem SSE-Adapter
 * (`useBusNotifications`), aus der Bridge oder aus App-Logik.
 */
export function NotificationProvider({ children, max = 50, initialItems }) {
    const [items, setItems] = useState(initialItems ?? []);
    const maxRef = useRef(max);
    maxRef.current = max;
    const add = useCallback((input) => {
        const id = input.id ?? nextId();
        const item = {
            id,
            title: input.title,
            body: input.body,
            severity: input.severity,
            timestamp: input.timestamp ?? Date.now(),
            read: input.read ?? false,
            source: input.source,
            entity: input.entity,
        };
        setItems((prev) => [item, ...prev].slice(0, maxRef.current));
        return id;
    }, []);
    const markRead = useCallback((id) => {
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    }, []);
    const markAllRead = useCallback(() => {
        setItems((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
    }, []);
    const remove = useCallback((id) => {
        setItems((prev) => prev.filter((n) => n.id !== id));
    }, []);
    const clear = useCallback(() => setItems([]), []);
    const store = useMemo(() => ({
        items,
        unreadCount: items.reduce((acc, n) => acc + (n.read ? 0 : 1), 0),
        add,
        markRead,
        markAllRead,
        remove,
        clear,
    }), [items, add, markRead, markAllRead, remove, clear]);
    return _jsx(NotificationContext.Provider, { value: store, children: children });
}
